/**
 * The one place this application talks to the server.
 *
 * Two jobs. It unwraps the spec 8 envelope so callers receive `data` and never
 * have to remember which key holds it, and it validates that what came back is
 * actually an envelope — because the most common failure in this deployment is
 * not a 500 from the API, it is a proxy or a login redirect answering with HTML.
 * Trusting `response.json()` to have the shape we expect turns that into
 * `undefined.map is not a function` three components away from the cause.
 */

export interface ApiMeta {
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

export interface Page<T> {
  readonly rows: readonly T[];
  readonly meta: ApiMeta | undefined;
}

/** Same prefix in dev (through the Vite proxy) and in production. */
const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    override readonly message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * The session is gone or was never presented. Distinguished from every other
   * failure because it is the only one the UI answers by navigating rather than
   * by showing a message.
   */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but this principal's scopes do not cover the action. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * There is no such endpoint on this server.
   *
   * Told apart from the other 4xx because it is not always a mistake: some routers
   * are mounted conditionally — `/settings` exists only where there is somewhere to
   * store an answer — so a 404 can mean "this deployment does not have that feature"
   * rather than "you asked wrongly". The server's message for it is "No such
   * endpoint", which is true and useless to an operator, so a view that expects this
   * case writes its own sentence.
   */
  get isMissing(): boolean {
    return this.status === 404;
  }

  /**
   * The server broke, rather than the request being wrong. Worth telling apart
   * because it changes what the operator should do: a 4xx is theirs to correct, a
   * 5xx is theirs to report — and the server's message carries the reference to
   * report it with (see `api/errorHandler.ts`).
   */
  get isServerFault(): boolean {
    return this.status >= 500;
  }
}

interface Envelope {
  success: boolean;
  data: unknown;
  error: unknown;
  meta?: unknown;
}

export interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
}

async function call(path: string, options: RequestOptions = {}): Promise<Envelope> {
  const url = `${BASE}${path}${buildQuery(options.query)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? "GET",
      // The session cookie is HttpOnly, so it is never read by this code — it
      // only has to be sent. Same-origin in both dev (proxied) and production.
      credentials: "same-origin",
      headers: options.body === undefined ? {} : { "content-type": "application/json" },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // A rejected fetch means the network or the server, not an API error. It has
    // no status, so it gets 0 rather than a status that would be a lie.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError("The server could not be reached.", 0);
  }

  const envelope = await readEnvelope(response);

  if (!response.ok || envelope === undefined || envelope.success !== true) {
    throw new ApiError(messageFrom(envelope, response), response.status);
  }

  return envelope;
}

/**
 * Reads the body at most once and never throws. A non-JSON body is not an
 * exception here — it is information, and the caller turns it into a status-
 * appropriate message rather than a parse error.
 */
async function readEnvelope(response: Response): Promise<Envelope | undefined> {
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return undefined;
  }

  if (typeof parsed !== "object" || parsed === null) return undefined;
  if (!("success" in parsed) || !("data" in parsed) || !("error" in parsed)) {
    return undefined;
  }

  return parsed as unknown as Envelope;
}

/**
 * The server's `error` string is written for an operator to read (spec 9 keeps
 * secrets and internals out of it), so it is shown verbatim when present.
 * Everything else falls back to a message written here, because a status code is
 * not a sentence.
 */
function messageFrom(envelope: Envelope | undefined, response: Response): string {
  if (envelope !== undefined && typeof envelope.error === "string" && envelope.error !== "") {
    return envelope.error;
  }

  if (response.status === 401) return "Your session has expired. Sign in again.";
  if (response.status === 403) return "Your account does not have access to this.";
  if (response.status === 429) return "Too many requests. Wait a moment and retry.";
  if (response.status >= 500) return "The server failed to handle that request.";

  return `Unexpected response from the server (HTTP ${response.status}).`;
}

function buildQuery(query: RequestOptions["query"]): string {
  if (query === undefined) return "";

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // An absent filter and an empty filter are the same request. Skipping
    // `undefined` here is what lets callers pass their state object directly.
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }

  const encoded = params.toString();
  return encoded === "" ? "" : `?${encoded}`;
}

/** A single resource. */
export async function get<T>(path: string, options?: RequestOptions): Promise<T> {
  const envelope = await call(path, options);
  return envelope.data as T;
}

/**
 * A list plus its pagination. `meta` is optional on the wire — unpaginated
 * endpoints like `/depots` omit it — so it stays optional here rather than being
 * invented with plausible-looking numbers.
 */
export async function getPage<T>(path: string, options?: RequestOptions): Promise<Page<T>> {
  const envelope = await call(path, options);
  const rows = Array.isArray(envelope.data) ? (envelope.data as readonly T[]) : [];

  return { rows, meta: readMeta(envelope.meta) };
}

export async function post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  const envelope = await call(path, {
    ...options,
    method: "POST",
    ...(body === undefined ? {} : { body }),
  });
  return envelope.data as T;
}

/**
 * A partial update. The one verb here whose *absence* of a field is meaningful:
 * the server reads a missing key as "unchanged", which is what lets an operator
 * switch a station off sale without restating the price it sold at.
 */
export async function patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  const envelope = await call(path, { ...options, method: "PATCH", body });
  return envelope.data as T;
}

/**
 * A whole replacement, and the counterpart to `patch`'s meaningful omissions: every
 * field is sent every time.
 *
 * `PUT /tariffs/:id` is the caller, and the distinction is the point there — the four
 * figures of a price are one decision, so "did the presets change" must not depend on
 * whether the client bothered to send them.
 */
export async function put<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  const envelope = await call(path, { ...options, method: "PUT", body });
  return envelope.data as T;
}

export async function del<T>(path: string, options?: RequestOptions): Promise<T> {
  const envelope = await call(path, { ...options, method: "DELETE" });
  return envelope.data as T;
}

function readMeta(raw: unknown): ApiMeta | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const candidate = raw as Record<string, unknown>;

  const { total, page, limit } = candidate;
  if (typeof total !== "number" || typeof page !== "number" || typeof limit !== "number") {
    return undefined;
  }

  return { total, page, limit };
}
